import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import { StatusPill, RiskPill } from "./MerchantOverview";

export default function MerchantApply() {
  const { formatApiError } = useAuth();
  const [existing, setExisting] = useState(null);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({
    legal_business_name: "", dba: "", website: "", industry: "Retail",
    monthly_volume: 50000, avg_ticket: 500, country: "US", bank_name: "",
    description: "", requested_limit: 25000,
  });
  const upd = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  useEffect(() => {
    api.get("/merchants/me").then(({ data }) => {
      if (data && data.merchant_id) setExisting(data);
    }).finally(() => setLoading(false));
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        ...form,
        monthly_volume: parseFloat(form.monthly_volume),
        avg_ticket: parseFloat(form.avg_ticket),
        requested_limit: parseFloat(form.requested_limit),
      };
      const { data } = await api.post("/merchants/apply", payload);
      setExisting(data);
      toast.success("Merchant application submitted for underwriting.");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    }
  };

  if (loading) return <div className="text-slate-500 text-sm">Loading...</div>;

  if (existing) {
    return (
      <div data-testid="merchant-apply-existing">
        <h1 className="font-heading text-3xl font-semibold tracking-tight">Merchant Profile</h1>
        <div className="sk-card p-8 mt-6">
          <div className="flex items-center justify-between flex-wrap gap-4">
            <div>
              <div className="text-xs uppercase tracking-wider text-slate-400">Status</div>
              <div className="mt-2 flex items-center gap-3">
                <StatusPill status={existing.status} />
                {existing.risk_tier && <RiskPill tier={existing.risk_tier} />}
              </div>
            </div>
            {existing.approved_limit && (
              <div>
                <div className="text-xs uppercase tracking-wider text-slate-400">Approved Limit</div>
                <div className="font-heading text-3xl mt-2">${Number(existing.approved_limit).toLocaleString()}</div>
              </div>
            )}
          </div>
          <div className="mt-8 grid sm:grid-cols-2 gap-6 text-sm">
            <Field label="Legal Name" value={existing.legal_business_name} />
            <Field label="DBA" value={existing.dba || "—"} />
            <Field label="Industry" value={existing.industry} />
            <Field label="Country" value={existing.country} />
            <Field label="Website" value={existing.website || "—"} />
            <Field label="Bank" value={existing.bank_name || "—"} />
            <Field label="Monthly Volume" value={`$${Number(existing.monthly_volume).toLocaleString()}`} />
            <Field label="Avg Ticket" value={`$${Number(existing.avg_ticket).toLocaleString()}`} />
            <Field label="Requested Limit" value={`$${Number(existing.requested_limit).toLocaleString()}`} />
          </div>
          {existing.description && (
            <div className="mt-6">
              <div className="text-xs uppercase tracking-wider text-slate-400">Description</div>
              <p className="mt-2 text-slate-700">{existing.description}</p>
            </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <div data-testid="merchant-apply-form">
      <p className="text-xs uppercase tracking-[0.18em] text-[#00D533]">Underwriting</p>
      <h1 className="font-heading text-3xl font-semibold tracking-tight mt-2">Merchant Application</h1>
      <p className="text-slate-500 mt-2 max-w-xl">
        Submit your business profile. Approval unlocks transaction limits, AML treatment and chargeback guarantees.
      </p>
      <form onSubmit={submit} className="mt-8 grid sm:grid-cols-2 gap-5 max-w-3xl">
        <FormInput label="Legal business name" required value={form.legal_business_name} onChange={upd("legal_business_name")} testid="apply-legal-name" />
        <FormInput label="DBA" value={form.dba} onChange={upd("dba")} testid="apply-dba" />
        <FormInput label="Website" value={form.website} onChange={upd("website")} testid="apply-website" />
        <FormInput label="Industry" required value={form.industry} onChange={upd("industry")} testid="apply-industry" />
        <FormInput label="Country code" value={form.country} onChange={upd("country")} testid="apply-country" />
        <FormInput label="Existing bank (optional)" value={form.bank_name} onChange={upd("bank_name")} testid="apply-bank" />
        <FormInput label="Monthly volume (USD)" type="number" required value={form.monthly_volume} onChange={upd("monthly_volume")} testid="apply-volume" />
        <FormInput label="Average ticket (USD)" type="number" required value={form.avg_ticket} onChange={upd("avg_ticket")} testid="apply-ticket" />
        <FormInput label="Requested limit per tx (USD)" type="number" required value={form.requested_limit} onChange={upd("requested_limit")} testid="apply-limit" />
        <div className="sm:col-span-2">
          <Label className="text-slate-600">Business description</Label>
          <Textarea value={form.description} onChange={upd("description")} className="mt-2 bg-[#FFFFFF] border-slate-200 min-h-[100px]" data-testid="apply-description" />
        </div>
        <div className="sm:col-span-2 mt-2">
          <Button type="submit" className="bg-[#00D533] hover:bg-[#00E636] text-black font-semibold h-11 px-7" data-testid="apply-submit-btn">
            Submit application
          </Button>
        </div>
      </form>
    </div>
  );
}

function FormInput({ label, testid, ...rest }) {
  return (
    <div>
      <Label className="text-slate-600">{label}</Label>
      <Input {...rest} className="mt-2 bg-[#FFFFFF] border-slate-200 h-11" data-testid={testid} />
    </div>
  );
}
function Field({ label, value }) {
  return (
    <div>
      <div className="text-xs uppercase tracking-wider text-slate-400">{label}</div>
      <div className="mt-1 text-slate-800">{value}</div>
    </div>
  );
}
